import Logo from "@/assets/images/Logo";
import CobbIcon from "@/assets/images/logos/cobb/cobb_tf.png";
import BDOIcon from "@/assets/images/logos/bdo/logo.png";
import NavTrialsIcon from "@/assets/images/logos/navtrials/logo.webp";
import ECRFIcon from "@/assets/images/logos/ecrf/ecrf_tf.svg?react";

import { ThemeConfig } from "antd";
import { getFirestore, getDoc, doc } from "firebase/firestore";
import { useState, useEffect } from "react";

type PartnerData = {
    key: string;
    shortName: string;
    fullName: string;
    logo: React.ReactNode;
    surveyUrl?: string;
    theme?: ThemeConfig;
    isDefault?: boolean;
    fontUrl?: string;
    headingFontFamily?: string;
    headerMenuTheme?: "dark" | "light";
};

const defaultData = {
    key: "default",
    shortName: "ResearchLinQ",
    fullName: "ResearchLinQ Trial Finder",
    logo: <Logo fill="black" preserveAspectRatio="xMidYMid meet" height="40" />,
    theme: {
        components: {
            Layout: {
                footerBg: "#e81484",
                colorBgLayout: "white",
            },
            Button: {
                defaultColor: "#e81484",
                defaultBorderColor: "#e81484",
                textTextColor: "#e81484",
            },
        },
        token: {
            colorPrimary: "#e81484",
            colorTextHeading: "#1567b0",
            colorHighlight: "#e81484",
        },
    },
    surveyUrl: "",
    isDefault: true,
};

// TODO: Move some of this data into DB
const partners = new Map<string, PartnerData>();
partners.set("cobb", {
    key: "cobb",
    shortName: "Cobb",
    fullName: "The Cobb Institute",
    logo: <img src={CobbIcon} alt="Helix logo" height={50} />,
    surveyUrl: "",
    theme: {
        token: {
            colorPrimary: "#bc9733",
            colorInfo: "#203F7F",
            colorTextHeading: "#203F7F",
            colorHighlight: "#bc9733",
        },
        components: {
            Collapse: {
                headerBg: "#bc9733",
            },
            Layout: {
                footerBg: "#203F7F",
            },
            Button: {
                defaultColor: "#bc9733",
                defaultBorderColor: "#bc9733",
                textTextColor: "#bc9733",
            },
        },
    },
});
partners.set("ecrf", {
    key: "ecrf",
    shortName: "ECRF",
    fullName: "Endometrial Cancer Research Foundation",
    logo: <ECRFIcon height={60} width={"fit-content"} style={{ width: "fit-content" }} />,
    surveyUrl: "https://form.jotform.com/260484142433149",
    theme: {
        token: {
            fontFamily: "Roboto",
            colorPrimary: "#290652",
            colorTextHeading: "#290652",
            colorInfo: "#f79860",
            colorLink: "#290652",
        },
        components: {
            Layout: {
                bodyBg: "#fcf1ed",
                footerBg: "white",
                headerBg: "white",
            },
            Menu: {
                itemColor: "#290652",
            },
            Button: {
                lineHeight: 32,
                paddingBlockLG: 16,
                paddingInlineLG: 32,
                paddingBlock: 32,
            },
        },
    },
});
partners.set("bdo", {
    key: "bdo",
    shortName: "BDO",
    fullName: "Black Doctor.org",
    logo: <img src={BDOIcon} alt="BDO Logo" height={50} />,
    surveyUrl: "",
    theme: {
        token: {
            colorPrimary: "#86ab42",
            colorInfo: "#97185f",
            colorTextHeading: "#97185f",
            colorHighlight: "#97185f",
        },
        components: {
            Button: {
                defaultColor: "#97185f",
                defaultHoverColor: "#b05482",
                defaultHoverBorderColor: "#b05482",
                defaultActiveColor: "#700c48",
                defaultActiveBorderColor: "#700c48",
                textTextColor: "#97185f",
                textTextHoverColor: "#b05482",
                textTextActiveColor: "#700c48",
            },
            Layout: {
                footerBg: "#222222",
            },
            Collapse: {
                headerBg: "#97185f",
            },
        },
    },
});
partners.set("navtrials", {
    key: "navtrials",
    shortName: "Nav Trials",
    fullName: "Navigating Trials",
    logo: <img src={NavTrialsIcon} alt="Navigating Trials Logo" height={50} />,
    surveyUrl: "",
    fontUrl:
        "https://fonts.googleapis.com/css2?family=Almarai:wght@300;400;700;800&family=Bricolage+Grotesque:opsz,wght@12..96,200..800&display=swap",
    headingFontFamily: "'Bricolage Grotesque', sans-serif",
    headerMenuTheme: "dark",
    theme: {
        token: {
            colorPrimary: "#354022",
            colorInfo: "#E5813B",
            colorTextHeading: "#E5813B",
            colorHighlight: "#E5813B",
            fontFamily: "'Almarai', sans-serif",
        },
        components: {
            Layout: {
                footerBg: "#354022",
                headerBg: "#354022",
            },
            Menu: {
                darkItemColor: "#ffffff",
                darkItemHoverColor: "#E5813B",
                darkItemSelectedColor: "#E5813B",
                darkItemBg: "transparent",
                darkPopupBg: "#354022",
            },
            Button: {
                colorPrimary: "#F47B20",
            },
        },
    },
});

async function fetchPartnerSubdomain(): Promise<string | undefined> {
    const db = getFirestore();
    try {
        const snapshot = await getDoc(doc(db, "trialFinderFeatureFlags", "showPartner"));
        if (snapshot.exists()) {
            return snapshot.data().subdomain as string;
        }
    } catch (error) {
        console.error("Failed to read feature flag:", error);
    }
    return undefined;
}
/**
 * Get the organization associated with the user from the URL, local, or session storage.
 * CAN ONLY BE USED IN A REACT COMPONENT UNDER THE ROUTER.
 * @returns The user's organization or undefined, if not set
 */

function getSubdomain(): string | undefined {
    const { hostname } = window.location;

    // This logic does not work for ip address. Don't split if it is
    if (
        /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(
            hostname
        )
    ) {
        return undefined;
    }

    const domains = hostname.split(".");

    switch (domains[0]) {
        // If the subdomain is not set
        case "staging":
        case "researchlinq":
        case "localhost":
            return undefined;
        default:
            return domains[0].trim().toLowerCase();
    }
}

function getUserOrganizationData(orgName?: string): PartnerData {
    const subdomain = orgName || getSubdomain();
    if (subdomain) {
        const partnerData = partners.get(subdomain);
        if (partnerData) {
            return partnerData;
        }
    }
    return defaultData;
}

function useUserOrganization(): { isLoading: boolean; userOrganization: PartnerData } {
    const [userOrganization, setUserOrganization] = useState<PartnerData>(getUserOrganizationData());
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (getSubdomain() === "partner") {
            setIsLoading(true);
            fetchPartnerSubdomain()
                .then((organization) => {
                    const updatedOrganization = getUserOrganizationData(organization);
                    setUserOrganization(updatedOrganization);
                })
                .catch((reason) => {
                    console.error(reason);
                    setUserOrganization(defaultData);
                })
                .finally(() => setIsLoading(false));
        }
    }, []);

    return { isLoading, userOrganization };
}

export default partners;
export { fetchPartnerSubdomain, getSubdomain as getUserOrganization, useUserOrganization };
export type { PartnerData };
